import type { SidebarState } from './types';
import { getApiKey, saveApiKey, clearApiKey } from './storage';
import { validateApiKey, trackRecorderOpened } from './api';

/**
 * Sidebar component - right-side docked panel (Django Debug Toolbar style)
 */
export class Sidebar {
  private container: HTMLDivElement;
  private toggleTab: HTMLButtonElement;
  private state: SidebarState;
  private siteId?: string;

  constructor(siteId?: string) {
    this.siteId = siteId;
    this.state = {
      isOpen: false,
      isVisible: false,
    };

    this.container = this.createSidebarElement();
    this.toggleTab = this.createToggleTab();
    this.injectStyles();
  }

  /**
   * Create the sidebar DOM element
   */
  private createSidebarElement(): HTMLDivElement {
    const sidebar = document.createElement('div');
    sidebar.id = 'warpway-sidebar';
    sidebar.className = 'warpway-sidebar warpway-sidebar-closed';

    sidebar.innerHTML = `
      <div class="warpway-sidebar-header">
        <h3>Warpway Dev Mode</h3>
        <button class="warpway-close-btn" aria-label="Close sidebar">×</button>
      </div>
      <div class="warpway-sidebar-content" id="warpway-sidebar-content">
        <!-- Content will be injected here -->
      </div>
    `;

    return sidebar;
  }

  /**
   * Set content of the sidebar
   */
  setContent(element: HTMLElement): void {
    const contentDiv = this.container.querySelector('#warpway-sidebar-content');
    if (contentDiv) {
      contentDiv.innerHTML = '';
      contentDiv.appendChild(element);
    }
  }

  /**
   * Create API key section UI
   */
  createApiKeySection(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'warpway-api-key-section';

    const apiKey = getApiKey();

    if (apiKey) {
      // Show success state - key is saved
      section.innerHTML = `
        <div class="warpway-success-message">
          <strong>API Key Connected</strong>
          <p>Your API key is saved and validated.</p>
        </div>
        <button class="warpway-btn warpway-btn-small warpway-btn-secondary" data-action="clear-api-key">
          Clear Key
        </button>
      `;
    } else {
      // Show input state - no key saved
      section.innerHTML = `
        <div class="warpway-form-group">
          <label for="warpway-api-key-input">API Key</label>
          <input
            type="password"
            id="warpway-api-key-input"
            class="warpway-input"
            placeholder="warp_..."
          />
        </div>
        <div id="warpway-api-key-error" class="warpway-error-message" style="display: none;"></div>
        <button class="warpway-btn warpway-btn-small warpway-btn-primary" data-action="save-api-key">
          Save API Key
        </button>
      `;
    }

    // Attach event listeners
    this.attachApiKeyEventListeners(section);

    return section;
  }

  /**
   * Attach event listeners for API key section
   */
  private attachApiKeyEventListeners(section: HTMLElement): void {
    // Save API Key button
    const saveBtn = section.querySelector('[data-action="save-api-key"]');
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        await this.handleSaveApiKey(section);
      });
    }

    // Clear API Key button
    const clearBtn = section.querySelector('[data-action="clear-api-key"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.handleClearApiKey();
      });
    }

    // Enter key on input
    const input = section.querySelector('#warpway-api-key-input') as HTMLInputElement;
    if (input) {
      input.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter') {
          await this.handleSaveApiKey(section);
        }
      });
    }
  }

  /**
   * Handle saving API key
   */
  private async handleSaveApiKey(section: HTMLElement): Promise<void> {
    const input = section.querySelector('#warpway-api-key-input') as HTMLInputElement;
    const errorDiv = section.querySelector('#warpway-api-key-error') as HTMLElement;
    const saveBtn = section.querySelector('[data-action="save-api-key"]') as HTMLButtonElement;

    const apiKey = input?.value.trim();

    if (!apiKey) {
      this.showError(errorDiv, 'Please enter an API key');
      return;
    }

    // Disable button and show loading state
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Validating...';
    }

    try {
      const isValid = await validateApiKey(apiKey);

      if (isValid) {
        saveApiKey(apiKey);

        // Track recorder opened if we have siteId
        if (this.siteId) {
          await trackRecorderOpened(apiKey, this.siteId);
        }

        // Refresh the sidebar to show success state
        this.refreshRecorderContent();
      } else {
        this.showError(errorDiv, 'Invalid API key. Please check and try again.');
      }
    } catch (error) {
      this.showError(
        errorDiv,
        error instanceof Error ? error.message : 'Failed to validate API key'
      );
    } finally {
      // Re-enable button
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save API Key';
      }
    }
  }

  /**
   * Handle clearing API key
   */
  private handleClearApiKey(): void {
    clearApiKey();
    this.refreshRecorderContent();
  }

  /**
   * Show error message
   */
  private showError(errorDiv: HTMLElement | null, message: string): void {
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
    }
  }

  /**
   * Create recorder content with API key section
   */
  createRecorderContent(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'warpway-recorder-container';

    const apiKey = getApiKey();

    if (!apiKey) {
      // Show consolidated step: Get API key and enter it
      const setupSection = document.createElement('div');
      setupSection.className = 'warpway-setup-section';
      setupSection.innerHTML = `
        <div class="warpway-step-header">
          <strong>Setup: Get Your API Key</strong>
        </div>
        <p class="warpway-step-description">
          Get your API key from <a href="#" data-action="open-keys-settings" class="warpway-link">Settings → API Keys</a> and enter it below to start recording.
        </p>
        <div class="warpway-form-group">
          <label for="warpway-api-key-input">API Key</label>
          <input
            type="password"
            id="warpway-api-key-input"
            class="warpway-input"
            placeholder="warp_..."
          />
        </div>
        <div id="warpway-api-key-error" class="warpway-error-message" style="display: none;"></div>
        <button class="warpway-btn warpway-btn-primary" data-action="save-api-key">
          Save & Continue
        </button>
      `;

      // Attach event listeners
      this.attachApiKeyEventListeners(setupSection);

      // Add link to open keys settings
      const openKeysLink = setupSection.querySelector('[data-action="open-keys-settings"]');
      if (openKeysLink) {
        openKeysLink.addEventListener('click', (e) => {
          e.preventDefault();
          window.open('/settings/keys', '_blank');
        });
      }

      container.appendChild(setupSection);
    } else {
      // Show API key connected status (collapsible)
      const apiKeyStatus = document.createElement('div');
      apiKeyStatus.className = 'warpway-api-key-status';
      apiKeyStatus.innerHTML = `
        <div class="warpway-success-message-compact">
          ✓ API Key Connected
          <button class="warpway-btn-link" data-action="clear-api-key">Change</button>
        </div>
      `;

      // Attach clear button event
      const clearBtn = apiKeyStatus.querySelector('[data-action="clear-api-key"]');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          this.handleClearApiKey();
        });
      }

      container.appendChild(apiKeyStatus);

      // Add divider
      const divider = document.createElement('div');
      divider.className = 'warpway-divider';
      container.appendChild(divider);

      // Show recorder controls
      const recorderControls = document.createElement('div');
      recorderControls.className = 'warpway-recorder-controls';
      recorderControls.innerHTML = `
        <div class="warpway-step-header">
          <strong>Start Recording</strong>
        </div>
        <p class="warpway-step-description">
          Click the button below to start recording user interactions.
        </p>
        <button class="warpway-btn warpway-btn-primary" data-action="start-recording">
          Start Recording
        </button>
      `;

      container.appendChild(recorderControls);
    }

    return container;
  }

  /**
   * Refresh recorder content
   */
  private refreshRecorderContent(): void {
    const content = this.createRecorderContent();
    this.setContent(content);
  }

  /**
   * Inject CSS styles into the page
   */
  private injectStyles(): void {
    const styleId = 'warpway-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .warpway-sidebar {
        position: fixed;
        top: 0;
        right: 0;
        width: 420px;
        height: 100vh;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        color: #f1f5f9;
        box-shadow: -8px 0 40px rgba(0, 0, 0, 0.5);
        z-index: 2147483647;
        display: flex;
        flex-direction: column;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
        font-size: 14px;
        line-height: 1.6;
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        backdrop-filter: blur(10px);
      }

      .warpway-sidebar-closed {
        transform: translateX(100%);
      }

      .warpway-sidebar-open {
        transform: translateX(0);
      }

      .warpway-sidebar-hidden {
        display: none !important;
      }

      .warpway-sidebar-header {
        padding: 24px 24px 20px 24px;
        background: rgba(15, 23, 42, 0.8);
        border-bottom: 1px solid rgba(100, 116, 139, 0.2);
        display: flex;
        justify-content: space-between;
        align-items: center;
        backdrop-filter: blur(10px);
      }

      .warpway-sidebar-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
        color: #fff;
        letter-spacing: -0.02em;
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .warpway-close-btn {
        background: rgba(51, 65, 85, 0.5);
        border: none;
        color: #94a3b8;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        transition: all 0.2s ease;
      }

      .warpway-close-btn:hover {
        background: rgba(239, 68, 68, 0.15);
        color: #f87171;
        transform: scale(1.05);
      }

      .warpway-sidebar-content {
        padding: 24px;
        overflow-y: auto;
        flex: 1;
        scrollbar-width: thin;
        scrollbar-color: rgba(100, 116, 139, 0.5) transparent;
      }

      .warpway-sidebar-content::-webkit-scrollbar {
        width: 6px;
      }

      .warpway-sidebar-content::-webkit-scrollbar-track {
        background: transparent;
      }

      .warpway-sidebar-content::-webkit-scrollbar-thumb {
        background: rgba(100, 116, 139, 0.5);
        border-radius: 3px;
      }

      .warpway-sidebar-content::-webkit-scrollbar-thumb:hover {
        background: rgba(100, 116, 139, 0.7);
      }

      .warpway-sidebar-content p {
        margin: 0 0 12px 0;
      }

      .warpway-toggle-tab {
        position: fixed;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        color: #fff;
        border: none;
        border-radius: 12px 0 0 12px;
        padding: 16px 10px;
        cursor: pointer;
        z-index: 2147483646;
        box-shadow: -4px 0 20px rgba(16, 185, 129, 0.4);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.05em;
        writing-mode: vertical-rl;
        text-orientation: mixed;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
      }

      .warpway-toggle-tab:hover {
        background: linear-gradient(135deg, #059669 0%, #0891b2 100%);
        right: 4px;
        box-shadow: -6px 0 30px rgba(16, 185, 129, 0.6);
        transform: translateY(-50%) scale(1.05);
      }

      .warpway-toggle-tab-hidden {
        display: none !important;
      }

      /* Onboarding styles */
      .warpway-onboarding {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .warpway-step {
        padding: 20px;
        background: rgba(30, 41, 59, 0.5);
        border-radius: 12px;
        border: 2px solid transparent;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        backdrop-filter: blur(10px);
      }

      .warpway-step-active {
        border-color: rgba(16, 185, 129, 0.5);
        background: rgba(15, 23, 42, 0.8);
        box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.1), 0 8px 24px rgba(16, 185, 129, 0.15);
      }

      .warpway-step-complete {
        border-color: rgba(100, 116, 139, 0.3);
        opacity: 0.6;
      }

      .warpway-step-disabled {
        opacity: 0.4;
      }

      .warpway-step-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, rgba(51, 65, 85, 0.6) 0%, rgba(71, 85, 105, 0.4) 100%);
        border: 2px solid rgba(100, 116, 139, 0.3);
        color: #94a3b8;
        border-radius: 12px;
        text-align: center;
        font-size: 14px;
        font-weight: 800;
        margin-bottom: 12px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
      }

      .warpway-step-number::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 12px;
        padding: 2px;
        background: linear-gradient(135deg, transparent 0%, transparent 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .warpway-step-active .warpway-step-number {
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        border-color: transparent;
        color: #fff;
        box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4), 0 0 0 3px rgba(16, 185, 129, 0.1);
        transform: scale(1.05);
      }

      .warpway-step-active .warpway-step-number::before {
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        opacity: 1;
      }

      .warpway-step-complete .warpway-step-number {
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%);
        border-color: rgba(16, 185, 129, 0.4);
        color: #10b981;
      }

      .warpway-step-title {
        margin: 8px 0 6px 0;
        font-size: 15px;
        font-weight: 700;
        color: #f1f5f9;
        letter-spacing: -0.01em;
      }

      .warpway-step-description {
        margin: 0 0 16px 0;
        font-size: 13px;
        color: #94a3b8;
        line-height: 1.5;
      }

      .warpway-step-hint {
        margin: 10px 0 0 0;
        font-size: 11px;
        color: #64748b;
        font-style: italic;
      }

      .text-muted {
        color: #888;
      }

      .warpway-btn {
        padding: 12px 18px;
        border-radius: 10px;
        border: none;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
        width: 100%;
        margin-bottom: 10px;
        letter-spacing: -0.01em;
      }

      .warpway-btn:last-child {
        margin-bottom: 0;
      }

      .warpway-btn-primary {
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        color: #fff;
        box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
      }

      .warpway-btn-primary:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
      }

      .warpway-btn-primary:active:not(:disabled) {
        transform: translateY(0);
      }

      .warpway-btn-primary:disabled {
        background: rgba(51, 65, 85, 0.5);
        color: #64748b;
        cursor: not-allowed;
        box-shadow: none;
      }

      .warpway-btn-secondary {
        background: rgba(51, 65, 85, 0.6);
        color: #cbd5e1;
        border: 1px solid rgba(100, 116, 139, 0.3);
      }

      .warpway-btn-secondary:hover:not(:disabled) {
        background: rgba(51, 65, 85, 0.8);
        border-color: rgba(100, 116, 139, 0.5);
      }

      .warpway-btn-text {
        background: transparent;
        color: #64748b;
        width: auto;
        padding: 10px 0;
        text-align: left;
        border-radius: 0;
      }

      .warpway-btn-text:hover {
        color: #10b981;
      }

      .warpway-input {
        width: 100%;
        padding: 12px 14px;
        background: rgba(15, 23, 42, 0.6);
        border: 2px solid rgba(100, 116, 139, 0.3);
        border-radius: 10px;
        color: #f1f5f9;
        font-size: 13px;
        font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
        margin-bottom: 16px;
        transition: all 0.2s ease;
      }

      .warpway-input::placeholder {
        color: #475569;
      }

      .warpway-input:focus {
        outline: none;
        border-color: #10b981;
        background: rgba(15, 23, 42, 0.8);
        box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
      }

      .warpway-input:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .warpway-btn-small {
        padding: 8px 14px;
        font-size: 13px;
      }

      .warpway-success-message {
        padding: 14px 16px;
        background: rgba(16, 185, 129, 0.1);
        border: 1px solid rgba(16, 185, 129, 0.3);
        border-left: 3px solid #10b981;
        border-radius: 10px;
        color: #6ee7b7;
        font-size: 13px;
        margin-bottom: 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .warpway-success-message strong {
        color: #10b981;
        font-weight: 600;
      }

      .warpway-success-message p {
        margin: 0;
        color: #94a3b8;
        font-size: 12px;
      }

      .warpway-success-message-compact {
        padding: 10px 14px;
        background: rgba(16, 185, 129, 0.1);
        border: 1px solid rgba(16, 185, 129, 0.3);
        border-radius: 8px;
        color: #6ee7b7;
        font-size: 13px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0;
      }

      .warpway-step-header {
        margin: 0 0 12px 0;
        font-size: 14px;
      }

      .warpway-step-header strong {
        color: #f1f5f9;
        font-weight: 600;
      }

      .warpway-btn-link {
        background: none;
        border: none;
        color: #6ee7b7;
        font-size: 12px;
        cursor: pointer;
        padding: 0;
        text-decoration: underline;
        transition: color 0.2s;
      }

      .warpway-btn-link:hover {
        color: #10b981;
      }

      .warpway-link {
        color: #6366f1;
        text-decoration: underline;
        cursor: pointer;
        transition: color 0.2s;
      }

      .warpway-link:hover {
        color: #818cf8;
      }

      .warpway-error-message {
        padding: 12px 14px;
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.3);
        border-left: 3px solid #ef4444;
        border-radius: 10px;
        color: #fca5a5;
        font-size: 13px;
        margin-bottom: 16px;
      }

      .warpway-warning-message {
        padding: 14px 16px;
        background: rgba(251, 191, 36, 0.1);
        border: 1px solid rgba(251, 191, 36, 0.3);
        border-left: 3px solid #fbbf24;
        border-radius: 10px;
        color: #fcd34d;
        font-size: 13px;
        margin-bottom: 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .warpway-warning-message strong {
        color: #fbbf24;
        font-weight: 600;
      }

      .warpway-warning-message p {
        margin: 0;
        color: #94a3b8;
        font-size: 12px;
      }

      .warpway-error {
        padding: 12px 14px;
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.3);
        border-left: 3px solid #ef4444;
        border-radius: 10px;
        color: #fca5a5;
        font-size: 13px;
        margin-bottom: 16px;
        display: flex;
        align-items: start;
        gap: 8px;
      }

      .warpway-error::before {
        content: '⚠';
        font-size: 16px;
        flex-shrink: 0;
      }

      .warpway-site-info {
        padding: 16px;
        background: rgba(15, 23, 42, 0.6);
        border: 1px solid rgba(100, 116, 139, 0.2);
        border-radius: 12px;
        margin-bottom: 20px;
      }

      .warpway-site-info-row {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        font-size: 13px;
      }

      .warpway-site-info-row:not(:last-child) {
        border-bottom: 1px solid rgba(100, 116, 139, 0.1);
      }

      .warpway-label {
        color: #64748b;
        font-weight: 500;
      }

      .warpway-value {
        color: #f1f5f9;
        font-weight: 600;
      }

      .warpway-divider {
        height: 1px;
        background: #404040;
        margin: 16px 0;
      }

      .warpway-actions {
        display: flex;
        justify-content: center;
      }

      .warpway-welcome {
        margin-bottom: 24px;
        padding: 16px;
        background: rgba(16, 185, 129, 0.1);
        border: 1px solid rgba(16, 185, 129, 0.2);
        border-radius: 12px;
      }

      .warpway-welcome p {
        margin: 0 0 8px 0;
        font-size: 15px;
        color: #f1f5f9;
        font-weight: 600;
      }

      .warpway-subtitle {
        font-size: 13px;
        color: #94a3b8;
        line-height: 1.5;
      }

      .warpway-footer {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid rgba(100, 116, 139, 0.2);
        text-align: center;
      }

      .warpway-link {
        color: #10b981;
        text-decoration: none;
        font-size: 13px;
        font-weight: 600;
        transition: all 0.2s ease;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      .warpway-link:hover {
        color: #059669;
        transform: translateY(-1px);
      }

      /* Recording UI Styles */
      .warpway-recording-header {
        padding: 20px;
        background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
        border: 2px solid rgba(239, 68, 68, 0.3);
        border-radius: 12px;
        margin-bottom: 20px;
      }

      .warpway-recording-indicator {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
      }

      .warpway-recording-dot {
        width: 12px;
        height: 12px;
        background: #ef4444;
        border-radius: 50%;
        animation: pulse-recording 2s infinite;
        box-shadow: 0 0 12px rgba(239, 68, 68, 0.6);
      }

      @keyframes pulse-recording {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.5;
          transform: scale(0.9);
        }
      }

      .warpway-recording-indicator h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        color: #f1f5f9;
      }

      .warpway-recording-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 13px;
        color: #94a3b8;
      }

      .warpway-sync-status {
        color: #10b981;
        font-weight: 600;
      }

      .warpway-recording-steps {
        max-height: 300px;
        overflow-y: auto;
        padding: 16px;
        background: rgba(15, 23, 42, 0.6);
        border: 1px solid rgba(100, 116, 139, 0.2);
        border-radius: 12px;
        margin-bottom: 20px;
      }

      .warpway-recorded-step {
        display: flex;
        gap: 12px;
        padding: 12px;
        background: rgba(30, 41, 59, 0.5);
        border-radius: 8px;
        margin-bottom: 8px;
        transition: all 0.2s ease;
      }

      .warpway-recorded-step:hover {
        background: rgba(30, 41, 59, 0.8);
        transform: translateX(4px);
      }

      .warpway-step-icon {
        font-size: 20px;
        flex-shrink: 0;
      }

      .warpway-step-details {
        flex: 1;
      }

      .warpway-step-type {
        font-size: 13px;
        font-weight: 700;
        color: #f1f5f9;
        margin-bottom: 2px;
        text-transform: capitalize;
      }

      .warpway-step-text {
        font-size: 12px;
        color: #94a3b8;
        font-style: italic;
      }

      .warpway-step-value {
        font-size: 12px;
        color: #06b6d4;
        font-family: 'Courier New', monospace;
      }

      .warpway-recording-actions {
        display: flex;
        gap: 12px;
      }

      /* Finalize UI Styles */
      .warpway-finalize-header {
        padding: 20px;
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.05) 100%);
        border: 2px solid rgba(16, 185, 129, 0.3);
        border-radius: 12px;
        margin-bottom: 20px;
        text-align: center;
      }

      .warpway-finalize-header h3 {
        margin: 0 0 8px 0;
        font-size: 18px;
        font-weight: 700;
        color: #f1f5f9;
      }

      .warpway-finalize-form {
        margin-bottom: 20px;
      }

      .warpway-form-group {
        margin-bottom: 16px;
      }

      .warpway-form-group label {
        display: block;
        margin-bottom: 8px;
        font-size: 13px;
        font-weight: 600;
        color: #f1f5f9;
      }

      .warpway-form-group textarea {
        resize: vertical;
        min-height: 80px;
        font-family: inherit;
      }

      .warpway-finalize-actions {
        display: flex;
        gap: 12px;
      }

      /* Phantom Click Styles */
      .warpway-phantom-controls {
        margin-bottom: 20px;
        padding: 16px;
        background: rgba(15, 23, 42, 0.6);
        border: 1px solid rgba(100, 116, 139, 0.2);
        border-radius: 12px;
      }

      .warpway-btn-phantom {
        background: rgba(51, 65, 85, 0.6);
        color: #cbd5e1;
        border: 1px solid rgba(100, 116, 139, 0.3);
        margin-bottom: 12px;
        transition: all 0.3s ease;
      }

      .warpway-btn-phantom:hover:not(:disabled) {
        background: rgba(16, 185, 129, 0.15);
        border-color: rgba(16, 185, 129, 0.5);
        color: #6ee7b7;
        transform: translateY(-2px);
      }

      .warpway-btn-phantom-active {
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        color: #fff;
        border-color: transparent;
        box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
        animation: warpway-phantom-pulse-btn 2s infinite;
      }

      @keyframes warpway-phantom-pulse-btn {
        0%, 100% {
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
        }
        50% {
          box-shadow: 0 4px 20px rgba(16, 185, 129, 0.5);
        }
      }

      .warpway-btn-phantom-active:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
      }

      .warpway-phantom-hint {
        margin: 0;
        font-size: 12px;
        color: #64748b;
        line-height: 1.5;
      }

      @media (max-width: 768px) {
        .warpway-sidebar {
          width: 100%;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Create the toggle tab button
   */
  private createToggleTab(): HTMLButtonElement {
    const tab = document.createElement('button');
    tab.className = 'warpway-toggle-tab warpway-toggle-tab-hidden';
    tab.setAttribute('aria-label', 'Open Warpway sidebar');
    tab.textContent = 'WARPWAY';
    return tab;
  }

  /**
   * Mount the sidebar to the DOM
   */
  mount(): void {
    if (document.body) {
      document.body.appendChild(this.container);
      document.body.appendChild(this.toggleTab);
      this.attachEventListeners();
    } else {
      // Wait for DOM to be ready
      document.addEventListener('DOMContentLoaded', () => {
        document.body.appendChild(this.container);
        document.body.appendChild(this.toggleTab);
        this.attachEventListeners();
      });
    }
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    const closeBtn = this.container.querySelector('.warpway-close-btn');
    closeBtn?.addEventListener('click', () => this.close());

    this.toggleTab.addEventListener('click', () => this.open());
  }

  /**
   * Show the sidebar (make visible in DOM)
   */
  show(): void {
    this.state.isVisible = true;
    this.container.classList.remove('warpway-sidebar-hidden');
    this.toggleTab.classList.remove('warpway-toggle-tab-hidden');
  }

  /**
   * Hide the sidebar (remove from view completely)
   */
  hide(): void {
    this.state.isVisible = false;
    this.container.classList.add('warpway-sidebar-hidden');
    this.toggleTab.classList.add('warpway-toggle-tab-hidden');
    this.close();
  }

  /**
   * Open the sidebar (slide in)
   */
  open(): void {
    if (!this.state.isVisible) return;

    this.state.isOpen = true;
    this.container.classList.remove('warpway-sidebar-closed');
    this.container.classList.add('warpway-sidebar-open');
    this.toggleTab.classList.add('warpway-toggle-tab-hidden');
  }

  /**
   * Close the sidebar (slide out)
   */
  close(): void {
    this.state.isOpen = false;
    this.container.classList.remove('warpway-sidebar-open');
    this.container.classList.add('warpway-sidebar-closed');

    // Only show toggle tab if sidebar is visible (not hidden)
    if (this.state.isVisible) {
      this.toggleTab.classList.remove('warpway-toggle-tab-hidden');
    }
  }

  /**
   * Toggle the sidebar open/closed
   */
  toggle(): void {
    if (!this.state.isVisible) return;

    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Check if sidebar is currently open
   */
  isOpen(): boolean {
    return this.state.isOpen;
  }

  /**
   * Check if sidebar is visible
   */
  isVisible(): boolean {
    return this.state.isVisible;
  }
}
